import { useCircleSelection } from "@/context/CircleSelectionContext";
import MainChat from "@/pages/chat/MainChat";
import MeetingModal from "@/pages/meeting/MeetingModal";
import { useAppDispatch, useAppSelector } from "@/redux/dispatch";
import { setBaseLocation, setLocations } from "@/redux/slice/locationSlice";
import { setMeetingStarts } from "@/redux/slice/meetingSlice";
import { RootState } from "@/redux/store/rootReducers";
import api from "@/services/api";
import { LocationApiResponse } from "@/types/location";
import { MenuIcon, X } from "lucide-react";
import { Suspense, useCallback, useEffect, useState } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Outlet } from "react-router-dom";
import { useAlertsPanel } from "../../context/AlertsPanelContext";
import AlertsGroupingPanel from "../alerts/AlertsGroupingPanel";
import AlertsPanel from "../alerts/AlertsPanel";
import LocationDropdown from "./LocationDropdown";
import Sidebar from "./Sidebar";
import { useAuth } from "@/context/AuthContext";

const MainLayout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { userDetails } = useAuth();

  const { isAlertsPanelOpen, isMainChatOpen, currentAlert } = useAlertsPanel();
  const { isGroupingPanelOpen } = useCircleSelection();
  const [isRightMenuOpen, setIsRightMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { locations, baseLocation } = useAppSelector((state: RootState) => state.location);
  const { isMeetingStart } = useAppSelector((state: RootState) => state.meeting);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(baseLocation || 'All');

  useEffect(() => {
    setIsRightMenuOpen(isMeetingStart)
  }, [isMeetingStart])

  const fetchAllLocations = useCallback(async () => {
    const offset = 0;
    const sortOrder = "ASC";
    const sortField = "locationID";
    const params = new URLSearchParams({
      offset: String(offset),
      sortField,
      sortOrder,
    });
    params.append('disabled', 'false')
    const allLocationStaticArray = [{
      locationID: 'All',
      locationName: 'All',
    }]
    const { data } = await api.get<LocationApiResponse>(`/locations?${params}`);

    if (data.data.length > 0) {
      dispatch(setLocations([...allLocationStaticArray, ...data.data]))
    }
  }, [dispatch])

  useEffect(() => {
    if (userDetails) {
      if (userDetails.userType === 'Administrator') {
        fetchAllLocations();
      }
      else {
        dispatch(setLocations([]));
        dispatch(setBaseLocation(userDetails?.locationID));
        setSelectedLocation(userDetails?.locationID);
      }
    }
  }, [userDetails, fetchAllLocations, dispatch])

  useEffect(() => {
    if (selectedLocation) {
      if (selectedLocation.trim() === 'All') {
        dispatch(setBaseLocation(''));
      }
      else {
        dispatch(setBaseLocation(selectedLocation));
      }
    }
  }, [selectedLocation, dispatch])

  return (
    <div className="h-screen overflow-hidden bg-[#FFFFFF] dark:bg-black relative flex transition-all duration-300">
      <div className="absolute inset-y-0 left-0 h-full flex">
        <div className="z-50">
          {!isRightMenuOpen &&
            <Sidebar
              isMobileMenuOpen={isMobileMenuOpen}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
            />
          }
        </div>
        <div className="z-40">
          {isAlertsPanelOpen && <AlertsPanel />}
          {isGroupingPanelOpen && <AlertsGroupingPanel />}
          {isMainChatOpen && currentAlert && (
            <MainChat alert={currentAlert} />
          )}
        </div>
      </div>

      <main
        className={`h-full overflow-hidden transition-all duration-300 flex flex-col sm:mx-auto sm:ml-0 lg:ml-[80px]`}
        style={{
          width: isRightMenuOpen ? "calc(100% - 250px)" : "100%",
        }}
      >
        <header className="fixed top-0 left-0 right-0 z-50 md:z-20 bg-dark lg:bg-white shadow-md h-[60px] md:h-[40px] px-4 flex items-center justify-between">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-white transition-all duration-300 hover:bg-dark/90"
          >
            {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
          <div className="text-xl w-full text-right flex justify-end mr-[5%]">
            <LocationDropdown locations={locations} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} />
          </div>
        </header>

        <div className="pt-[60px] md:pt-[40px]  w-full flex-1 overflow-y-auto">
          <Suspense fallback={
            <div className="h-full w-full flex items-center justify-center bg-white dark:bg-black">
              <LoadingSpinner size="medium" color="rgb(5 83 156)" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </div>
      </main>

      <aside
        className={`h-full bg-gray-900 text-white shadow-lg transition-all duration-300 z-50 ${isRightMenuOpen ? "w-[45vw]" : "w-0"
          }`}
      >
        {isRightMenuOpen && (
          <MeetingModal
            onClose={() => dispatch(setMeetingStarts(false))}
          />
        )}
      </aside >
    </div >
  );
};

export default MainLayout;
